import moment from 'moment-timezone';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card as DSCard } from '@hivebrite/design-system/Card';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { useTheme, useMatchBreakpoint } from '@hivebrite/design-system/Theme';

import { Calendar } from 'hb-react/shared/apps/Events/components/Calendar';
import { EventWebinarUrl } from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventWebinarUrl';
import type { Event } from 'hb-react/shared/apps/Events/types';
import EventDate from 'hb-react/shared/components/EventDate';
import Image from 'hb-react/shared/components/Image';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';

import { Details, HeaderColumn, HeaderRow } from './components';

export const Card = ({ event }: { event: Event }) => {
  const {
    start_date: startDate,
    end_date: endDate,
    timezone,
    cover_picture_medium: coverPictureMedium,
    title,
    locations,
    webinar_url: webinarUrl,
    show_webinar_url: showWebinarUrl,
  } = event;

  const isMedium = useMatchBreakpoint('m');
  const theme = useTheme();
  const { flagEvOrderSummaryDsEvents } = useFeatureFlag();
  const timeZoneConfiguration = useTimeZoneConfiguration();

  const day = moment(startDate).tz(timezone).date();

  if (flagEvOrderSummaryDsEvents) {
    return (
      <DSCard px={4}>
        <Box mb={6} alignItems="center">
          <Box alignSelf="center">
            <Calendar startDate={startDate} eventDisplayTimeZone={timezone} />
          </Box>
          <Box ml={4} font="h3-semibold">
            {title}
          </Box>
        </Box>
        <Box>
          <Dotdotdot clamp={1}>
            <Box as="span" display="inline" fontWeight={2}>
              {__('Date:')}{' '}
            </Box>
            <EventDate
              as="span"
              event={{ start_date: startDate, end_date: endDate, timezone }}
              timezoneConfiguration={timeZoneConfiguration}
            />
          </Dotdotdot>
        </Box>
        {locations?.address && (
          <Box mt={{ _: 2, m: 4 }}>
            <Dotdotdot clamp={1}>
              <Box as="span" display="inline" fontWeight={2}>
                {__('Location:')}{' '}
              </Box>{' '}
              {locations.address}
            </Dotdotdot>
          </Box>
        )}
        {webinarUrl && (
          <Box mt={{ _: 2, m: 4 }}>
            <Box
              as="span"
              display="inline"
              fontWeight={2}
              style={{ whiteSpace: 'nowrap' }}
            >
              {__('Online event')}
              {showWebinarUrl ? ':' : ''}
            </Box>
            <EventWebinarUrl
              webinar_url={webinarUrl}
              show_webinar_url={showWebinarUrl}
            />
          </Box>
        )}
      </DSCard>
    );
  }

  return (
    <Box flexDirection="column">
      <ImageWrapper display={{ _: 'none', l: 'block' }}>
        <Image
          src={coverPictureMedium}
          alt=""
          style={{
            borderTopLeftRadius: theme.themeVariables.globalBorderRadius,
            borderTopRightRadius: theme.themeVariables.globalBorderRadius,
          }}
        />
      </ImageWrapper>

      {isMedium ? (
        <Box px={4} py={5}>
          <MediumScreenContent {...event} day={day} />
        </Box>
      ) : (
        <Box p={4} flexDirection="column">
          <SmallAndLargeScreenContent {...event} day={day} />
        </Box>
      )}
    </Box>
  );
};

const SmallAndLargeScreenContent = ({
  start_date: startDate,
  timezone,
  end_date: endDate,
  locations,
  webinar_url: webinarUrl = '',
  show_webinar_url: showWebinarUrl = false,
  day,
  title,
}) => (
  <>
    <HeaderColumn day={day} title={title} />
    <Details
      startDate={startDate}
      timezone={timezone}
      endDate={endDate}
      locations={locations}
      webinarUrl={webinarUrl}
      showWebinarUrl={showWebinarUrl}
    />
  </>
);

const MediumScreenContent = ({
  start_date: startDate,
  timezone,
  end_date: endDate,
  locations,
  webinar_url: webinarUrl = '',
  show_webinar_url: showWebinarUrl = false,
  day,
  title,
}) => (
  <>
    <Box width="50%">
      <Box maxWidth="100%" display="block" pr={3}>
        <HeaderRow
          day={day}
          title={title}
          startDate={startDate}
          endDate={endDate}
          timezone={timezone}
        />
      </Box>
    </Box>
    <Box width="50%">
      <Box maxWidth="100%" display="block" pl={3}>
        <Details
          startDate={startDate}
          timezone={timezone}
          endDate={endDate}
          locations={locations}
          webinarUrl={webinarUrl}
          showWebinarUrl={showWebinarUrl}
        />
      </Box>
    </Box>
  </>
);

const ImageWrapper = styled(Box)`
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;
